/** How the server names a channel and admits this tab to it. */
export interface CollaborationChannelAccess {
  channel: string;
  token: string;
}

/** How a person is shown to the others in the poster. Vouched for by the server inside the subscription token. */
export interface CollaborationParticipant {
  userId: number;
  name: string;
  avatarUrl: string;
}

/** The response of stream/getStreamToken: the connection token and the namespace it is for. */
export interface StreamTokenResponse {
  token: string;
  ns: string;
}

/** The response of stream/getPosterCollaborationAccess. */
export interface PosterCollaborationAccess {
  edits: CollaborationChannelAccess;
  presence: CollaborationChannelAccess;
  participant: CollaborationParticipant;
}

export interface CanvasPoint {
  x: number;
  y: number;
}

/** An axis-aligned rectangle in a page's unscaled canvas pixels. */
export interface CanvasRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * What one editor tab publishes about itself on the presence channel. Positions are in page
 * canvas coordinates, so every viewer projects them through its own zoom and scroll. The
 * selection travels as item ids only: each viewer outlines those items where they lie in its own
 * poster, so the outline never leaves the item while the other tab is still mid-gesture.
 */
export interface PresencePayload {
  sessionId: string;
  pageId: string | null;
  cursor: CanvasPoint | null;
  selectedItemIds: string[];
}

export enum PresenceMessageType {
  UPDATE = 'update',
  LEAVE = 'leave',
}

export interface PresenceUpdateMessage extends PresencePayload {
  type: PresenceMessageType.UPDATE;
}

export interface PresenceLeaveMessage {
  type: PresenceMessageType.LEAVE;
  sessionId: string;
}

/** What travels on the presence channel. */
export type PresenceMessage = PresenceUpdateMessage | PresenceLeaveMessage;

/** Another tab in the same poster, as the overlay and the roster render it. One person in two tabs is two sessions. */
export interface RemoteSession extends PresencePayload {
  participant: CollaborationParticipant;
  color: string;
  lastSeenAt: number;
}

export type RemoteSessionsById = Record<string, RemoteSession>;

/** What a session, page or item id looks like (uuids and hex hashes); anything else from a peer is refused before it can reach a lookup. */
export const PRESENCE_ENTITY_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/** A peer's selection is capped: every id costs the overlay a lookup and a measurement per render. */
export const MAX_PRESENCE_SELECTED_ITEM_IDS = 200;

/** How often a tab looks for a change worth publishing. Ten times a second keeps cursors smooth without flooding the channel. */
export const PRESENCE_PUBLISH_INTERVAL_MS = 100;

/** An unchanged state is still republished this often, so the others know the tab is alive. */
export const PRESENCE_HEARTBEAT_INTERVAL_MS = 3_000;

/** A session that has published nothing for this long is dropped even if its leave message was lost. */
export const REMOTE_SESSION_STALE_MS = 30_000;

/** How often stale sessions are looked for. */
export const REMOTE_SESSION_PRUNE_INTERVAL_MS = 2_000;

/** Cursor and selection colours (the co-editing design's palette), assigned per session so two tabs of one person are still told apart. */
export const PRESENCE_COLORS = ['#810bff', '#bb0064', '#00688c', '#00664e', '#d39800', '#403f5d'] as const;
